import { TakeoverModal } from '../TakeoverModal';

import styles from './Disclosure.module.css';

interface DisclosureProps {
  practiceName: string;
  onClose: () => void;
}
export const Disclosure: React.FC<DisclosureProps> = ({
  practiceName,
  onClose,
}) => {
  return (
    <TakeoverModal onClose={onClose} className={styles.DisclosureModal}>
      <div>
        <Section header="ELECTRONIC RECORD AND SIGNATURE DISCLOSURE">
          From time to time, {practiceName} (we, us or Company) may be required
          by law to provide to you certain written notices or disclosures.
          Described below are the terms and conditions for providing to you such
          notices and disclosures electronically through the Notable system.
          Please read the information below carefully and thoroughly, and if you
          can access this information electronically to your satisfaction and
          agree to this Electronic Record and Signature Disclosure (ERSD),
          please confirm your agreement by selecting the check-box next to ‘I
          agree to use electronic records and signatures’ before clicking ‘ADD
          SIGNATURE’ within the Notable system.
        </Section>
        <Section header="Withdrawing your consent">
          If you decide to receive notices and disclosures from us
          electronically, you may at any time change your mind and tell us that
          thereafter you want to receive required notices and disclosures only
          in paper format. How you must inform us of your decision to receive
          future notices and disclosure in paper format and withdraw your
          consent to receive notices and disclosures electronically is described
          below.
        </Section>

        <Section header="Consequences of changing your mind">
          If you elect to receive required notices and disclosures only in paper
          format, it may slow the speed at which we can deliver services to you.
          Further, you will no longer be able to use the Notable system to
          receive required notices and consents electronically from us or to
          sign electronically documents from us.
        </Section>
        <Section header="All notices and disclosures will be sent to you electronically">
          Unless you tell us otherwise in accordance with the procedures
          described herein, we will provide electronically to you through the
          Notable system all required notices, disclosures, authorizations,
          acknowledgements, and other documents that are required to be provided
          or made available to you during the course of our relationship with
          you. To reduce the chance of you inadvertently not receiving any
          notice or disclosure, we prefer to provide all of the required notices
          and disclosures to you by the same method and to the same address that
          you have given us. If you do not agree with this process, please let
          us know as described below. Please also see the paragraph immediately
          above that describes the consequences of your electing not to receive
          delivery of the notices and disclosures electronically from us.
        </Section>
        <Section header="Getting paper copies">
          You will have the ability to download and print documents we send to
          you via email immediately after the signing session. At any time, you
          may request a paper copy of any record provided or made available
          electronically to you by us by contacting the doctor’s office
          associated with your visit.
        </Section>

        <Section header={`How to contact ${practiceName}`}>
          You may contact us to let us know of your changes as to how we may
          contact you electronically, to request paper copies of certain
          information from us, and to withdraw your prior consent to receive
          notices and disclosures electronically as follows: To contact us by
          phone, call the doctor’s office associated with your visit.
        </Section>
        <Section header={`To advise ${practiceName} of your new email address`}>
          To let us know of a change in your email address where we should send
          notices and disclosures electronically to you, simply change the email
          address when prompted before adding your signature. We do not require
          any other information from you to change your email address.
        </Section>

        <Section header={`To request paper copies from ${practiceName}`}>
          To request delivery from us of paper copies of the notices and
          disclosures previously provided by us to you electronically, contact
          the doctor’s office associated with your visit.
        </Section>
        <Section header={`To withdraw your consent with ${practiceName}`}>
          To inform us that you no longer wish to receive future notices and
          disclosures in electronic format you may:
          <ol>
            <li>
              decline to sign a document from within your signing session, or
              you may;
            </li>
            <li>
              contact the doctor’s office associated with your visit and let
              them know that you wish to consent via paper copies instead of
              electronically. We do not need any other information from you to
              withdraw consent. The consequences of your withdrawing consent for
              online documents will be that transactions may take a longer time
              to process.
            </li>
          </ol>
        </Section>
        <Section header="Required hardware and software">
          The minimum system requirements for using the Notable system may
          change over time. The current system requirements are:
          <div>
            <b>Browsers:</b>
          </div>
          <ul>
            <li>Google Chrome Current Version</li>
            <li>Safari (Mac OS only) 12.0 or above</li>
            <li>Windows Edge Current Version</li>
            <li>Internet Explorer Current Version (Windows only)</li>
            <li>Mozilla Firefox Current Version</li>
            <li>
              Note: Pre-release (e.g., beta) versions of operating systems and
              browsers are not supported.
            </li>
          </ul>
          <div>
            <b>Mobile Signing:</b>
          </div>
          <ul>
            <li>Apple iOS 12.0 or above</li>
            <li>Android 9.0 or above</li>
          </ul>
          <div>
            <b>PDF Reader:</b>
          </div>
          Acrobat Reader or similar software might be required to view and PDF
          files.
        </Section>
        <Section header="Acknowledging your access and consent to receive and sign documents electronically">
          <p>
            To confirm to us that you can access this information
            electronically, which will be similar to other electronic notices
            and disclosures that we will provide to you, please confirm that you
            have read this ERSD, and (i) that you are able to print on paper or
            electronically save this ERSD for your future reference and access;
            or (ii) that you are able to email this ERSD to an email address
            where you will be able to print on paper or save it for your future
            reference and access. Further, if you consent to receiving notices
            and disclosures exclusively in electronic format as described
            herein, then select the check-box next to ‘I agree to use electronic
            records and signatures’ before clicking ‘ADD SIGNATURE’ within the
            Notable system.
          </p>

          <p>
            By selecting the check-box next to ‘I agree to use electronic
            records and signatures’, you confirm that:
          </p>

          <ul>
            <li>
              You can access and read this Electronic Record and Signature
              Disclosure; and
            </li>
            <li>
              You can print on paper this Electronic Record and Signature
              Disclosure, or save or send this Electronic Record and Disclosure
              to a location where you can print it, for future reference and
              access; and
            </li>
            <li>
              Until or unless you notify {practiceName} as described above, you
              consent to receive exclusively through electronic means all
              notices, disclosures, authorizations, acknowledgements, and other
              documents that are required to be provided or made available to
              you by {practiceName} during the course of your relationship with{' '}
              {practiceName}.
            </li>
          </ul>
        </Section>
      </div>
    </TakeoverModal>
  );
};

interface SectionProps extends React.PropsWithChildren {
  header: React.ReactNode;
}
const Section: React.FC<SectionProps> = ({ header, children }) => {
  return (
    <div className={styles.Section}>
      <div className={styles.Header} children={header} />
      {children}
    </div>
  );
};
